import { Button, colors, Dropdown, IconGraphy, Loading, Table, Tooltip } from '@leapeasy/ui-kit';
import { Typography } from '@leapeasy/ui-kit';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { Checkbox } from '@mui/material';
import { DisputeSuccessModal } from 'components/DisputeSuccessModal';
import { useNavigate } from 'react-router-dom';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import {
  getInvoiceLineListAction,
  sendDisputeToAdminAction,
  setPolicyAction,
  clearDisputeSuccessAction,
} from 'store/actions/invoiceActions';
import { fetchInvoicePolicy, getInvoiceLineList } from 'store/sagas/APIs/invoice';
import { getRiderIdFromDescription } from 'utils/invoiceLineUtils';
import { openDetails } from 'store/actions/uiActions';
import { failedNotif } from 'store/actions/notifActions';
import { ListExportScopeModal } from 'components/Tables/shared/ListExportScopeModal';
import { fetchAllPaginated } from 'utils/exportPagination';
import { GlobalUXState } from 'components/GlobalUXState';
import { INVOICE_OFF_ITEMS } from 'data/constants/common_constants';
import { useDemoMask } from 'hooks/useDemoMask';
import { buildMaskedInvoiceLineRow } from 'utils/demoMask';
import { fitContentTextColumnOptions } from 'utils/fitContentTextColumnOptions';
import styled from 'styled-components';

const INVOICE_LINES_QUERY_PARAMS = {
  search: 'ilSearch',
  page: 'ilPage',
  pageSize: 'ilPageSize',
  sort: 'ilSort',
};

export const CurrentInvoiceTable = ({ data, dropFilterActiveCount, dropFilterActiveSummary }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isDemo } = useDemoMask();

  const { filter, setFilter, sortOptions, setSortOptions, pagination, setPagination } =
    useListQueryState({
      defaultFilter: {
        options: {},
        searchText: '',
        searchPlaceholder: 'Search Application..',
      },
      defaultRowsPerPage: 12,
      params: INVOICE_LINES_QUERY_PARAMS,
    });

  const [currentInvoice, setCurrentInvoice] = useState([]);

  const [totalItems, setTotalItems] = useState(0);

  const [tableData, setTableData] = useState([]);
  const invoiceLineData = useSelector((state) => state.getIn(['invoice', 'invoiceLineData']));
  const lineData = invoiceLineData?.get('data');
  const lineDispute = invoiceLineData?.get('dispute');
  const lineTotal = invoiceLineData?.get('total');
  const lineListLoading = Boolean(invoiceLineData?.get('isLoading'));
  const lineListError = invoiceLineData?.get('lineListError') || null;
  const disputeSubmit = useSelector((state) => state.getIn(['invoice', 'disputeSubmit']));
  const disputeSubmitJSON = disputeSubmit?.toJS?.() ?? {
    isSubmitting: false,
    showSuccessModal: false,
    hasClaimFiledReason: false,
  };
  const normalizedSearchText = `${filter?.searchText || filter?.search || ''}`.trim();
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const tableRef = useRef(null);
  const [fullExportDatasetTotal, setFullExportDatasetTotal] = useState(undefined);
  const pageResetBaselineRef = useRef(null);

  const fetchLineList = useCallback(() => {
    if (!data?.invoice_id) return;
    dispatch(
      getInvoiceLineListAction({
        invoice_id: data.invoice_id,
        filter: {
          ...filter,
          searchText: normalizedSearchText,
        },
        sortOptions,
        offset: pagination.pageNumber * pagination.rowsPerPage,
        limit: pagination.rowsPerPage,
      }),
    );
  }, [
    data?.invoice_id,
    dispatch,
    filter,
    normalizedSearchText,
    pagination.pageNumber,
    pagination.rowsPerPage,
    sortOptions,
  ]);

  useEffect(() => {
    if (!data?.invoice_id) {
      setCurrentInvoice([]);
      setTotalItems(0);
      return;
    }
    fetchLineList();
  }, [data?.invoice_id, fetchLineList]);

  useEffect(() => {
    const next = {
      search: filter?.searchText ?? '',
      sortKey: JSON.stringify(sortOptions ?? {}),
      invoiceId: data?.invoice_id,
    };
    const prev = pageResetBaselineRef.current;
    pageResetBaselineRef.current = next;
    if (prev == null) return;

    if (
      prev.search === next.search &&
      prev.sortKey === next.sortKey &&
      prev.invoiceId === next.invoiceId
    ) {
      return;
    }

    setPagination((prevPage) =>
      prevPage.pageNumber === 0 ? prevPage : { ...prevPage, pageNumber: 0 },
    );
  }, [filter?.searchText, sortOptions, data?.invoice_id, setPagination]);

  useClampListPagination({
    pagination,
    setPagination,
    totalItems: lineTotal,
    isLoading: lineListLoading,
  });

  useEffect(() => {
    if (!data?.invoice_id) {
      setFullExportDatasetTotal(undefined);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await getInvoiceLineList({
          invoice_id: data.invoice_id,
          filter: { searchText: '' },
          sortOptions: {},
          offset: 0,
          limit: 1,
        });
        if (!cancelled && response?.total != null) {
          setFullExportDatasetTotal(Number(response.total));
        }
      } catch (e) {
        // ignore — full export total is optional metadata
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data?.invoice_id]);

  useEffect(() => {
    const rows = lineData?.toJS ? lineData.toJS() : lineData || [];
    const fetchedRows = rows.map((invoice) => ({
      ...invoice,
      flg: {
        checked: false,
        index: invoice.row_index,
        reason: '',
      },
    }));

    setCurrentInvoice(fetchedRows);
    setTotalItems(Number(lineTotal) || 0);
  }, [lineData, lineTotal]);

  useEffect(() => {
    const tmpTblData = [];
    const disputeData = lineDispute?.toJS ? lineDispute.toJS() : lineDispute || [];
    currentInvoice.forEach((invoice, index) => {
      const dispute_filter = disputeData.length
        ? disputeData.filter(
            (e) => e.description === invoice.Description && e.service_date === invoice.ServiceDate,
          )
        : [];
      tmpTblData.push(
        buildMaskedInvoiceLineRow(
          invoice,
          dispute_filter[0] ? dispute_filter[0].reason : invoice.flg,
          isDemo,
        ),
      );
    });
    setTableData(tmpTblData);
  }, [currentInvoice, lineDispute, isDemo]);

  const mapInvoiceLineToExportRow = useCallback(
    (invoice, index) =>
      buildMaskedInvoiceLineRow(
        {
          ...invoice,
          flg: { checked: false, index, reason: '' },
        },
        '',
        isDemo,
      ),
    [isDemo],
  );

  const handleRequestExportData = useCallback(async () => {
    const invoiceId = data?.invoice_id;
    const totalRecords = Number(totalItems) || 0;
    if (!invoiceId || totalRecords === 0) return [];
    try {
      const rows = await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getInvoiceLineList({
            invoice_id: invoiceId,
            filter: {
              ...filter,
              searchText: normalizedSearchText,
            },
            sortOptions,
            offset,
            limit,
          }),
      });
      return rows.map((invoice, index) => mapInvoiceLineToExportRow(invoice, index));
    } catch (e) {
      throw new Error('Unable to export the current invoice lines.');
    }
  }, [
    data?.invoice_id,
    totalItems,
    filter,
    normalizedSearchText,
    sortOptions,
    mapInvoiceLineToExportRow,
  ]);

  const handleRequestFullExportData = useCallback(async () => {
    const invoiceId = data?.invoice_id;
    const totalRecords = Number(fullExportDatasetTotal) || Number(totalItems) || 0;
    if (!invoiceId || totalRecords === 0) return [];
    try {
      const rows = await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getInvoiceLineList({
            invoice_id: invoiceId,
            filter: {
              ...filter,
              searchText: '',
            },
            sortOptions,
            offset,
            limit,
          }),
      });
      return rows.map((invoice, index) => mapInvoiceLineToExportRow(invoice, index));
    } catch (e) {
      throw new Error('Unable to export the full invoice dataset.');
    }
  }, [
    data?.invoice_id,
    fullExportDatasetTotal,
    totalItems,
    filter,
    sortOptions,
    mapInvoiceLineToExportRow,
  ]);

  const reason_list = INVOICE_OFF_ITEMS.map((reason) => ({
    text: reason,
    value: reason,
  }));

  const [policyDetailLoadingRiderId, setPolicyDetailLoadingRiderId] = useState(null);
  const checkedRowsCount = currentInvoice.filter((item) => item?.flg?.checked).length;
  const selectedWithReasonCount = currentInvoice.filter(
    (item) => item?.flg?.checked && item?.flg?.reason,
  ).length;

  const updateCurrentInvoiceFlag = (lineIndex, updater) => {
    setCurrentInvoice((prev) =>
      prev.map((item) => {
        if (item?.flg?.index !== lineIndex) return item;
        return {
          ...item,
          flg: updater(item.flg),
        };
      }),
    );
  };

  const onClickNotifyLeap = () => {
    if (!data?.invoice_id) {
      dispatch(failedNotif('Invoice detail is unavailable.'));
      return;
    }
    if (disputeSubmitJSON.isSubmitting) return;

    const filtered_value = currentInvoice.filter((item) => item?.flg?.checked && item?.flg?.reason);
    if (filtered_value.length === 0) {
      dispatch(failedNotif('Select at least one invoice line and reason before notifying Leap.'));
      return;
    }

    const disputeData = lineDispute?.toJS ? lineDispute.toJS() : lineDispute || [];
    const payload = filtered_value.map((item) => {
      const dispute_filter = disputeData.length
        ? disputeData.filter(
            (e) => e.description === item.Description && e.service_date === item.ServiceDate,
          )
        : [];
      return {
        row_index: item.flg.index,
        invoice_id: data.invoice_id,
        invoice_line_id: item.LineNum,
        description: item.Description,
        amount: item.Amount,
        detail_type: dispute_filter[0] ? dispute_filter[0].reason : '',
        reason: item.flg.reason,
        total_amount: data.total_amount,
        building_name: data.sf_building_name,
        service_date: item.ServiceDate,
      };
    });

    dispatch(sendDisputeToAdminAction(payload));
  };

  const onClickDetail = async (description) => {
    const riderId = getRiderIdFromDescription(description);
    if (!riderId) {
      dispatch(failedNotif('Rider ID was not found in this invoice line description.'));
      return;
    }
    if (policyDetailLoadingRiderId) return;

    setPolicyDetailLoadingRiderId(riderId);
    try {
      const policy = await fetchInvoicePolicy(riderId);
      if (!policy?.app_id) {
        dispatch(
          failedNotif(policy ? 'Policy detail ID is missing.' : `No policy found for ${riderId}.`),
        );
        return;
      }

      dispatch(setPolicyAction(policy));
      dispatch(
        openDetails({
          type: 'policy',
          data: policy,
        }),
      );
      navigate(`/property/policies/${policy.app_id}/detail`);
    } catch (error) {
      dispatch(
        failedNotif(error?.response?.data?.message || `Failed to load policy for ${riderId}.`),
      );
    } finally {
      setPolicyDetailLoadingRiderId(null);
    }
  };

  useEffect(() => {
    if (!disputeSubmitJSON.showSuccessModal) return;
    fetchLineList();
    setCurrentInvoice((prev) =>
      prev.map((item) => ({
        ...item,
        flg: {
          ...item.flg,
          checked: false,
          reason: '',
        },
      })),
    );
  }, [disputeSubmitJSON.showSuccessModal, fetchLineList]);

  const handleCloseDisputeSuccess = () => {
    dispatch(clearDisputeSuccessAction());
  };

  const lineTotalNum = Number(totalItems) || 0;

  return (
    <CurrentInvoiceWrapper>
      <DisputeSuccessModal
        open={Boolean(disputeSubmitJSON.showSuccessModal)}
        onClose={handleCloseDisputeSuccess}
        hasClaimFiledReason={Boolean(disputeSubmitJSON.hasClaimFiledReason)}
      />
      {selectedWithReasonCount > 0 ? (
        <ActionButtonContainer>
          <Typography
            variant="h3"
            style={{
              fontSize: '16px',
              color: colors.purple[900],
              fontWeight: '700',
              padding: '5px 5px 5px 15px',
            }}
          >
            {`${checkedRowsCount} row(s) selected`}
          </Typography>
          <Button
            size="medium"
            variant="primary"
            style={{
              width: '150px',
            }}
            disabled={disputeSubmitJSON.isSubmitting}
            onClick={onClickNotifyLeap}
          >
            {disputeSubmitJSON.isSubmitting ? 'Sending...' : 'Notify Leap'}
          </Button>
        </ActionButtonContainer>
      ) : null}
      {lineListError && !lineListLoading ? (
        <Box display="flex" flexDirection="column" alignItems="center" gap="16px" mb={2}>
          <GlobalUXState variant="apiError" compact description={lineListError} />
          <Button variant="primary" size="medium" onClick={fetchLineList}>
            Retry
          </Button>
        </Box>
      ) : null}
      {lineListLoading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <Loading size="large" />
        </Box>
      ) : null}
      <Table
        showEmptyState
        emptyContent={
          <GlobalUXState
            variant={normalizedSearchText ? 'noResults' : 'noData'}
            compact
            description={
              normalizedSearchText
                ? 'No invoice lines match your search.'
                : 'No invoice lines are available for this invoice.'
            }
          />
        }
        ref={tableRef}
        onExportButtonClick={() => setExportModalOpen(true)}
        onRequestExportData={handleRequestExportData}
        onRequestFullExportData={handleRequestFullExportData}
        title="Current Invoice"
        data={tableData}
        autoTooltipForDefaultCells
        filter={filter}
        dropFilter={{
          resetFunc: () => {},
          applyFunc: () => {},
        }}
        dropFilterActiveCount={dropFilterActiveCount}
        dropFilterActiveSummary={dropFilterActiveSummary}
        onChangeFilter={setFilter}
        onChangeRowsPerPage={(value) =>
          setPagination((prev) => ({
            ...prev,
            rowsPerPage: Number(value),
            pageNumber: 0,
          }))
        }
        pagination={{
          ...pagination,
          totalItems: lineTotalNum,
          totalPages: Math.ceil(lineTotalNum / pagination.rowsPerPage) || 0,
        }}
        onChangePagination={setPagination}
        sortOptions={sortOptions}
        onChangeSort={setSortOptions}
        style={{ width: '100%' }}
        columns={[
          {
            name: '#',
            options: {
              flex: '56px 0 0',
              customBodyRenderer: (value, tableMeta) => {
                return (
                  <CheckboxCell>
                    <Checkbox
                      disableRipple
                      checked={value.checked}
                      onChange={(e) => {
                        updateCurrentInvoiceFlag(value.index, (currentFlag) => ({
                          ...currentFlag,
                          checked: e.target.checked,
                          reason: e.target.checked ? currentFlag.reason : '',
                        }));
                      }}
                      sx={{
                        color: '#69236b',
                        '&.Mui-checked .MuiSvgIcon-root': {
                          borderColor: '#69236b',
                          color: '#69236b',
                        },
                      }}
                    ></Checkbox>
                  </CheckboxCell>
                );
              },
            },
            key: 'select',
          },
          {
            name: 'Service Date',
            options: {
              flex: '120px 1 1',
              sort: true,
            },
            key: 'ServiceDate',
          },
          {
            name: 'Description',
            options: {
              ...fitContentTextColumnOptions({
                maxWidth: 520,
              }),
              allowOverflow: true,
              sort: true,
              customBodyRenderer: (value) => (
                <Typography variant="body2">{value || '-'}</Typography>
              ),
            },
            key: 'Description',
          },
          {
            name: 'Amount',
            options: {
              flex: '120px 1 1',
              sort: true,
            },
            key: 'Amount',
          },
          {
            name: 'Reason',
            options: {
              flex: '212px 1 1',
              allowOverflow: true,
              customBodyRenderer: (value, tableMeta) => {
                return typeof value === 'string' && !!value ? (
                  value
                ) : value.checked ? (
                  <SelectContainer className="tbl-dropdown">
                    {value.checked && (
                      <Dropdown
                        icon={
                          <IconGraphy
                            color="#3C393D"
                            height={12}
                            icon="HomePlaces.MapsHomeWork"
                            width={12}
                          />
                        }
                        class
                        onChange={(e) => {
                          updateCurrentInvoiceFlag(value.index, (currentFlag) => ({
                            ...currentFlag,
                            reason: e.target.value,
                          }));
                        }}
                        value={value.reason}
                        showSearch={false}
                        options={reason_list}
                        placeholder="Select Item"
                        menuMaxHeight="168px"
                        style={{
                          width: '100%',
                          minWidth: '0',
                        }}
                      />
                    )}
                  </SelectContainer>
                ) : (
                  // currentInvoice[value.index].flg.reason
                  value.reason
                );
              },
            },
            key: 'Reason',
          },
          {
            name: 'Detail',
            options: {
              flex: '15px 1 1',
              customBodyRenderer: (value) => {
                const riderId = getRiderIdFromDescription(value);
                const isLoadingPolicy = riderId && policyDetailLoadingRiderId === riderId;

                return (
                  <Tooltip
                    title={
                      riderId
                        ? isLoadingPolicy
                          ? 'Loading policy details...'
                          : 'View policy details'
                        : 'No rider ID found for this line'
                    }
                    placement="top"
                    showCarot
                  >
                    <div>
                      <DetailIconButton
                        type="button"
                        aria-label={riderId ? 'View policy details' : 'Policy details unavailable'}
                        disabled={!riderId || Boolean(policyDetailLoadingRiderId)}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!riderId || policyDetailLoadingRiderId) return;
                          void onClickDetail(value);
                        }}
                      >
                        <IconGraphy
                          icon={'FileFolder.Description'}
                          style={{
                            color: riderId ? '#702572' : '#B9B9C3',
                            opacity: isLoadingPolicy ? 0.5 : 1,
                          }}
                        />
                      </DetailIconButton>
                    </div>
                  </Tooltip>
                );
              },
            },
            key: 'detail',
          },
        ]}
      />
      <ListExportScopeModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        tableRef={tableRef}
        title="Current Invoice"
        currentPageRowCount={tableData.length}
        paginationTotalItems={lineTotalNum}
        fullDatasetTotal={fullExportDatasetTotal}
        enableFiltered
        enableFull
        enableSelected={false}
      />
    </CurrentInvoiceWrapper>
  );
};

const CurrentInvoiceWrapper = styled.div`
  .tbl-dropdown {
    width: 100%;
    min-width: 0;
  }
`;

const SelectContainer = styled.div`
  width: 100%;
  min-width: 0;
  & > div {
    width: 100%;
    min-width: 0;
    & > div:nth-of-type(2) {
      display: none;
    }
  }
`;

const ActionButtonContainer = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  gap: 20px;
`;

const DetailIconButton = styled.button`
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;

const CheckboxCell = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
